import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TabViewItem } from '@interfaces/a-organizar/tabViewItem';
import { CorporateRegistryCacheService } from '@services/http/corporate-registry/corporate-registry-cache.service';
import { SharedModule } from '@shared/shared.module';
import { TabViewComponent } from '@shared/components/tab-view/tab-view.component';
import { RegistryNetworkCardComponent } from '@shared/components/ownership-network/registry-network-card/registry-network-card.component';
import { DealRegistryPanelComponent } from '@src/app/features/dashboard/commercial/pages/deal-detail/components/registry/deal-registry-panel/deal-registry-panel.component';
import { CharterActsTimelineComponent } from '@features/dashboard/company/corporate-charter/components/charter-acts-timeline/charter-acts-timeline.component';
import { CharterCurrentViewComponent } from '@features/dashboard/company/corporate-charter/components/charter-current-view/charter-current-view.component';
import { CharterDocumentViewerComponent } from '@features/dashboard/company/corporate-charter/components/charter-document-viewer/charter-document-viewer.component';
import { CharterDataRoomPendingComponent } from '@features/dashboard/company/corporate-charter/components/charter-data-room-pending/charter-data-room-pending.component';
import { CharterCompanySwitcherComponent } from '@features/dashboard/company/corporate-charter/components/charter-company-switcher/charter-company-switcher.component';
import { CharterSummaryStripComponent } from '@features/dashboard/company/corporate-charter/components/charter-summary-strip/charter-summary-strip.component';
import { CharterFormerCardComponent } from '@features/dashboard/company/corporate-charter/components/charter-former-card/charter-former-card.component';
import {
  CHARTER_BREADCRUMB_HOME,
  CHARTER_COMPANY_BREADCRUMBS,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';
import { CharterDocumentViewerService } from '@features/dashboard/company/corporate-charter/services/charter-document-viewer.service';
import { CompanyCorporateCharterPageState } from './company-corporate-charter-page.state';

/**
 * Tela "Societário da empresa" (`/dashboard/company/:id/societario`, PBI 3260): o contrato social vigente montado a
 * partir dos atos lidos pelo Cog no Data Room (Ltda e S.A.). Abas: **Contrato vigente** (cada valor com a procedência
 * e o histórico), **Linha do tempo** (o que cada ato mudou; modelo, custo e sessão do agente só para o time interno),
 * **Ex-sócios e ex-administradores** e **Receita e Mapa societário** (cadastro e sócios do painel de registro do deal + o mapa societário compartilhado, aberto
 * na visão Controle). O documento original
 * abre na página do valor pelo `app-charter-document-viewer`.
 */
@Component({
  selector: 'app-company-corporate-charter-page',
  imports: [
    SharedModule,
    TabViewComponent,
    DealRegistryPanelComponent,
    RegistryNetworkCardComponent,
    CharterCurrentViewComponent,
    CharterActsTimelineComponent,
    CharterFormerCardComponent,
    CharterDocumentViewerComponent,
    CharterSummaryStripComponent,
    CharterCompanySwitcherComponent,
    CharterDataRoomPendingComponent,
  ],
  providers: [
    CompanyCorporateCharterPageState,
    CorporateRegistryCacheService,
    CharterDocumentViewerService,
  ],
  templateUrl: './company-corporate-charter-page.component.html',
  styleUrl: './company-corporate-charter-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyCorporateCharterPageComponent {
  protected readonly state = inject(CompanyCorporateCharterPageState);
  private readonly viewer = inject(CharterDocumentViewerService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  /** Índice da aba "Receita e Mapa societário", a única que não depende do vigente. */
  protected readonly registryTab = 3;

  protected readonly breadcrumbHome = CHARTER_BREADCRUMB_HOME;
  protected readonly breadcrumbs = CHARTER_COMPANY_BREADCRUMBS;

  protected readonly tabs = computed<TabViewItem[]>(() => [
    { title: 'Contrato vigente', tabindex: 0, content: '' },
    { title: 'Linha do tempo', tabindex: 1, content: '', count: this.state.actsCount() },
    {
      title: 'Ex-sócios e ex-administradores',
      tabindex: 2,
      content: '',
      count: this.state.formerCount(),
    },
    { title: 'Receita e Mapa societário', tabindex: this.registryTab, content: '' },
  ]);
  protected readonly activeTab = signal(0);

  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);

  /**
   * Ato pedido pelo link `?act=` ("Abrir item" do dashboard de custos de IA, card 3411). Aceita o id do ato ou o
   * id do documento do Data Room, que é o que a sessão de leitura do Cog grava como fonte.
   */
  private readonly requestedAct = signal<string | null>(
    this.route.snapshot.queryParamMap.get('act')
  );

  /** O ato pedido já foi mostrado: o destaque abre a aba uma vez só, sem prender o usuário nela. */
  private requestedActShown = false;

  /** Id do ato destacado na linha do tempo; nulo enquanto os atos não chegaram ou sem link. */
  protected readonly highlightedActId = computed(() => {
    const requested = this.requestedAct()?.toLowerCase();
    if (!requested) return null;
    const refs = [...this.state.acts(), ...(this.state.charter()?.timeline ?? [])];
    const match = refs.find(
      ref => ref.actId?.toLowerCase() === requested || ref.documentId?.toLowerCase() === requested
    );
    return match?.actId ?? null;
  });

  constructor() {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.state.setCompany(params.get('id')));

    // `?act=`: abre a "Linha do tempo" e rola até o ato quando os atos chegam.
    effect(() => {
      const actId = this.highlightedActId();
      if (!actId || this.requestedActShown) return;
      this.requestedActShown = true;
      this.activeTab.set(1);
      // `afterNextRender` não pode nascer dentro do contexto reativo do effect.
      untracked(() =>
        afterNextRender(
          () =>
            this.document
              .getElementById(`charter-act-${actId}`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
          { injector: this.injector }
        )
      );
    });
  }

  /** "Abrir documento vigente": o documento do ato mais recente aplicado, no visualizador. */
  protected openCurrentDocument(): void {
    const current = this.state.currentAct();
    const actDate = this.state
      .charter()
      ?.timeline?.find(entry => entry.actId === current?.actId)?.actDate;
    if (current) {
      this.viewer.open({
        actId: current.actId,
        actLabel: current.label,
        documentId: current.documentId,
        documentName: current.documentName,
        actDate: actDate ?? current.registeredAt ?? current.signedAt,
      });
    }
  }
}
